import React from 'react';
import { Info, CheckCircle, XCircle, MessageCircle, Award, Target, Square } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const NegotiationDosAndDonts = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">Negotiation Do's and Don'ts</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Essential Overview</h2>
              <p className="text-gray-700">
                Navigating job offer negotiations can be tricky, but with the right strategies, 
                you can secure the best possible terms. This guide provides essential do's and 
                don'ts to help you negotiate confidently and professionally.
              </p>
            </div>
          </div>
        </div>

        {/* Do's Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Do's: Best Practices for Successful Negotiation
          </h2>

          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <div className="flex items-start gap-4">
              <Target className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="font-semibold mb-4">1. Research Market Rates</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Know the typical salary range for your role, industry, and location</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Use tools like Glassdoor, Payscale, or industry reports to benchmark your value</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <div className="flex items-start gap-4">
              <Award className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="font-semibold mb-4">2. Know Your Value</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Highlight your skills, experience, and unique contributions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Use quantifiable achievements to justify your salary expectations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Additional Do's */}
          {[
            {
              title: "3. Practice Your Pitch",
              items: [
                "Rehearse your negotiation points to ensure clarity and confidence",
                "Prepare responses to potential objections from the employer"
              ]
            },
            {
              title: "4. Negotiate the Entire Package",
              items: [
                "Consider other benefits like bonuses, vacation days, remote work, or professional development",
                "Be ready to discuss non-salary perks that matter to you"
              ]
            },
            {
              title: "5. Express Enthusiasm",
              items: [
                "Show excitement about the role and company while advocating for your needs",
                {
                  example: "I'm thrilled about this opportunity and confident my skills will make a strong impact. I'd like to discuss how the compensation package aligns with my expectations."
                }
              ]
            },
            {
              title: "6. Ask for Written Offers",
              items: [
                "Ensure all verbal agreements are documented to avoid misunderstandings"
              ]
            },
            {
              title: "7. Stay Professional",
              items: [
                "Maintain a positive and respectful tone throughout the discussion"
              ]
            }
          ].map((section, index) => (
            <div key={index} className="bg-blue-50 p-6 rounded-lg mb-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold mb-4">{section.title}</h3>
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      typeof item === 'string' ? (
                        <li key={i} className="flex items-start gap-3">
                          <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ) : (
                        <div key={i} className="bg-white p-4 rounded-lg mt-2">
                          <p className="text-gray-600 italic">"{item.example}"</p>
                        </div>
                      )
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Don'ts Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <XCircle className="w-6 h-6 text-red-600" />
            Don'ts: Common Pitfalls to Avoid
          </h2>

          {[
            {
              title: "1. Don't Negotiate Without Preparation",
              items: [
                "Avoid entering discussions without researching market data or knowing your desired terms"
              ]
            },
            {
              title: "2. Don't Accept the First Offer Immediately",
              items: [
                "Even if the offer seems good, take time to evaluate it fully",
                "Politely request 1-2 days to review the terms"
              ]
            },
            {
              title: "3. Don't Make It Personal",
              items: [
                "Focus on the value you bring, not personal financial needs",
                {
                  example: "Avoid statements like, 'I need this salary to cover my bills.'"
                }
              ]
            },
            {
              title: "4. Don't Use Ultimatums",
              items: [
                "Avoid issuing 'take it or leave it' statements, which can come across as aggressive"
              ]
            },
            {
              title: "5. Don't Fabricate Offers",
              items: [
                "Never lie about having competing offers to gain leverage; this can damage your credibility"
              ]
            },
            {
              title: "6. Don't Overfocus on Salary",
              items: [
                "Be open to other aspects of the compensation package that could add value"
              ]
            },
            {
              title: "7. Don't Rush the Process",
              items: [
                "Allow the employer time to review and respond to your requests without pressure"
              ]
            }
          ].map((section, index) => (
            <div key={index} className="bg-red-50 p-6 rounded-lg mb-6">
              <div className="flex items-start gap-4">
                <XCircle className="w-5 h-5 text-red-600" />
                <div>
                  <h3 className="font-semibold mb-4">{section.title}</h3>
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      typeof item === 'string' ? (
                        <li key={i} className="flex items-start gap-3">
                          <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ) : (
                        <div key={i} className="bg-white p-4 rounded-lg mt-2">
                          <p className="text-gray-600 italic">{item.example}</p>
                        </div>
                      )
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Example Script Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-blue-600" />
            Example Negotiation Script
          </h2>

          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="font-semibold mb-4">Scenario: The initial offer is below your expectations</h3>
            <div className="space-y-6">
              <div className="bg-white p-4 rounded-lg">
                <p className="font-medium text-blue-800 mb-2">You:</p>
                <p className="text-gray-600 italic">"Thank you for the offer. I'm very excited about the opportunity to join your team. Based on my research and the value I bring, I was hoping for a salary closer to [desired amount]. Is there flexibility in the budget?"</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-medium text-blue-800 mb-2">Employer:</p>
                <p className="text-gray-600 italic">"We'll need to review, but we're close to our limit."</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-medium text-blue-800 mb-2">You:</p>
                <p className="text-gray-600 italic">"I understand, and I appreciate your consideration. I'm confident we can find a solution that works for both sides. Additionally, I'd love to discuss other elements of the package, such as professional development opportunities or additional PTO."</p>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="bg-gray-100 p-6 rounded-lg">
          <p className="text-gray-700">
            By following these do's and don'ts, you'll navigate job offer negotiations with confidence 
            and professionalism, ensuring a favorable outcome for both you and your future employer.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NegotiationDosAndDonts;